import { useState, useEffect, useCallback } from 'react';
import { validateClientForm } from '../utils/validation/clientValidation';
import { clientesService } from '../services';
import { useDebounce } from './useDebounce';

/**
 * Hook personalizado para manejar operaciones CRUD de clientes
 */
export const useClientes = () => {
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Simular API endpoints - reemplazar con llamadas reales
  const API_ENDPOINTS = {
    getAll: '/api/clientes',
    getById: (id) => `/api/clientes/${id}`,
    create: '/api/clientes',
    update: (id) => `/api/clientes/${id}`,
    delete: (id) => `/api/clientes/${id}`
  };

  /**
   * Transformar datos de la API al formato esperado
   */
  const transformClienteFromAPI = useCallback((cliente) => ({
    id: cliente.id,
    nombreNegocio: cliente.company_name || cliente.full_name,
    nombreContacto: cliente.full_name || '',
    telefono: cliente.phone || '',
    email: cliente.email || '',
    categoria: cliente.client_category ? (cliente.client_category === 'DISTRIBUTOR' ? 'distributor' : 'tienda') : '',
    tipoCliente: cliente.business_type || '',
    vendedorAsignado: cliente.assigned_user_id ? cliente.assigned_user_id.toString() : '',
    fechaRegistro: cliente.created_at || '',
    ultimaCompra: cliente.last_purchase || '',
    totalCompras: cliente.total_purchases || 0,
    facturasPendientes: cliente.pending_invoices || 0,
    montoFacturasPendientes: cliente.pending_amount || 0,
    status: cliente.is_active ? 'activo' : 'inactivo'
  }), []);

  /**
   * Cargar todos los clientes
   */
  const loadClientes = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros por defecto para la consulta
      const defaultParams = {
        skip: 0,
        limit: 50,
        is_active: true,
        ...params
      };
      
      const result = await clientesService.getClientes(defaultParams);
      
      if (result.success) {
        // Transformar los datos de la API al formato esperado
        const clientesData = result.data.map(transformClienteFromAPI);
        setClientes(clientesData);
      } else {
        setError(result.message || 'Error al cargar los clientes');
        setClientes([]);
      }
    } catch (err) {
      setError('Error de conexión al cargar los clientes');
      console.error('Error loading clientes:', err);
      setClientes([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformClienteFromAPI]);

  /**
   * Buscar clientes (actualiza query, la búsqueda real se hace con debounce)
   */
  const searchClientes = useCallback((query) => {
    setSearchQuery(query);
  }, []);
  
  /**
   * Realizar búsqueda real en la API (se ejecuta con debounce)
   */
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, cargar todos los clientes
      loadClientes();
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros de búsqueda para la API
      const searchParams = {
        skip: 0,
        limit: 50,
        is_active: true,
        search: query
      };
      
      const result = await clientesService.getClientes(searchParams);
      
      if (result.success) {
        const clientesData = result.data.map(transformClienteFromAPI);
        setClientes(clientesData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setClientes([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching clientes:', err);
      setClientes([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadClientes, transformClienteFromAPI]);

  /**
   * Eliminar cliente
   */
  const deleteCliente = useCallback(async (clienteId) => {
    try {
      // TODO: Reemplazar con llamada real a la API
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Verificar si el cliente tiene facturas pendientes
      const cliente = clientes.find(c => c.id === clienteId);
      if (cliente && cliente.facturasPendientes > 0) {
        return {
          success: false,
          error: `No se puede eliminar ${cliente.nombreNegocio} porque tiene ${cliente.facturasPendientes} factura(s) pendiente(s)`
        };
      }

      // Simular eliminación
      const updatedClientes = clientes.filter(c => c.id !== clienteId);
      setClientes(updatedClientes);

      return {
        success: true,
        message: 'Cliente eliminado exitosamente'
      };
    } catch (err) {
      console.error('Error deleting cliente:', err);
      return {
        success: false,
        error: 'Error inesperado al eliminar el cliente'
      };
    }
  }, [clientes]);

  /**
   * Obtener estadísticas de clientes
   */
  const getClientesStats = useCallback(() => {
    const stats = {
      total: clientes.length,
      activos: clientes.filter(c => c.status === 'activo').length,
      conFacturasPendientes: clientes.filter(c => c.facturasPendientes > 0).length,
      totalFacturasPendientes: clientes.reduce((sum, c) => sum + (c.montoFacturasPendientes || 0), 0)
    };
    return stats;
  }, [clientes]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar clientes al montar el componente
  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  return {
    clientes,
    isLoading,
    error,
    searchQuery,
    loadClientes,
    deleteCliente,
    searchClientes,
    getClientesStats,
    clearError
  };
};

/**
 * Hook para manejar un cliente individual (crear/editar)
 */
export const useCliente = (clienteId = null) => {
  const [cliente, setCliente] = useState({
    // Información básica REQUERIDA
    fullName: '',
    email: '',
    phone: '',
    mobile: '',
    phones: null, // Array de teléfonos con tipo

    // Tipo y estado
    clientType: 'company', // Siempre será empresa/negocio
    status: 'lead', // lead por defecto

    // Información de empresa (requerida si es company)
    companyName: '',
    taxId: '',
    industry: '',
    
    // Dirección de negocio
    businessAddress: '',
    businessUnit: '',
    businessCity: '',
    businessState: '',
    businessCountry: 'Estados Unidos', // Estados Unidos por defecto
    businessPostalCode: '',

    // Dirección de entrega
    sameDeliveryAddress: true,
    deliveryAddress: '',
    deliveryUnit: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryCountry: '',
    deliveryPostalCode: '',
    isResidential: false,
    
    // Categorización REQUERIDA
    clientCategory: 'TIENDA', // Por defecto TIENDA
    businessType: 'BETEL_NATURAL', // Por defecto BETEL NATURAL

    // Información de impuestos
    requiresTaxes: false,

    // Información de pagos
    paymentType: 'COD', // COD o TERMINOS - Por defecto COD
    paymentTerms: 0,
    creditLimit: 0,
    creditDays: 0,
    discountPercentage: 0,

    // Tipo de envío
    shippingType: 'UPS', // Por defecto UPS
    
    // Vendedor asignado REQUERIDO
    assignedUserId: 1, // Valor por defecto
    
    // Seguimiento
    source: '',
    firstContactDate: null,
    lastContactDate: null,
    
    // Contactos adicionales
    additionalContacts: [],
    
    // Metadatos
    notes: '',
    tags: '',
    
    // Portal
    portalAccess: false
  });
  
  const [originalCliente, setOriginalCliente] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const isEditing = Boolean(clienteId);

  /**
   * Cargar datos del cliente (solo en modo edición)
   */
  const loadCliente = useCallback(async () => {
    if (!clienteId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await clientesService.getCliente(clienteId);
      
      if (result.success) {
        // Transformar los datos de la API al formato esperado por el formulario
        const clienteData = {
          id: result.data.id,
          // Información básica
          fullName: result.data.full_name || '',
          email: result.data.email || '',
          phone: result.data.phone || '',
          mobile: result.data.mobile || '',
          phones: result.data.phones || result.data.phone_list || null,

          // Tipo y estado
          clientType: result.data.client_type || 'individual',
          status: result.data.status || 'lead',
          
          // Información de empresa
          companyName: result.data.company_name || '',
          taxId: result.data.tax_id || '',
          industry: result.data.industry || '',
          
          // Dirección de negocio
          businessAddress: result.data.business_address || '',
          businessUnit: result.data.business_unit || '',
          businessCity: result.data.business_city || '',
          businessState: result.data.business_state || '',
          businessCountry: result.data.business_country || 'Estados Unidos',
          businessPostalCode: result.data.business_postal_code || '',

          // Dirección de entrega
          sameDeliveryAddress: result.data.same_delivery_address !== false,
          deliveryAddress: result.data.delivery_address || '',
          deliveryUnit: result.data.delivery_unit || '',
          deliveryCity: result.data.delivery_city || '',
          deliveryState: result.data.delivery_state || '',
          deliveryCountry: result.data.delivery_country || '',
          deliveryPostalCode: result.data.delivery_postal_code || '',
          isResidential: result.data.is_residential || false,
          
          // Categorización
          clientCategory: result.data.client_category || '',
          businessType: result.data.business_type || 'BETEL_NATURAL',
          
          // Impuestos y pagos
          requiresTaxes: result.data.requires_taxes || false,
          paymentType: result.data.payment_type || 'TERMINOS',
          paymentTerms: result.data.payment_terms || 30,
          creditLimit: result.data.credit_limit || 0,
          creditDays: result.data.credit_days || 0,
          discountPercentage: result.data.discount_percentage || 0,
          
          // Envío y asignación
          shippingType: result.data.shipping_type || '',
          assignedUserId: result.data.assigned_user_id || 1,
          
          // Seguimiento
          source: result.data.source || '',
          firstContactDate: result.data.first_contact_date || null,
          lastContactDate: result.data.last_contact_date || null,
          
          // Contactos y metadatos
          additionalContacts: result.data.additional_contacts || [],
          notes: result.data.notes || '',
          tags: result.data.tags || '',
          portalAccess: result.data.portal_access || false
        };

        setCliente(clienteData);
        setOriginalCliente({ ...clienteData });
      } else {
        setError(result.message || 'Error al cargar los datos del cliente');
      }
    } catch (err) {
      setError('Error de conexión al cargar los datos del cliente');
      console.error('Error loading cliente:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clienteId]);

  /**
   * Actualizar datos del cliente
   */
  const updateClienteData = useCallback((field, value) => {
    setCliente(prev => {
      const updated = { ...prev, [field]: value };
      
      // Verificar si hay cambios
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(originalCliente);
      setHasChanges(hasChanges);
      
      return updated;
    });
  }, [originalCliente]);

  /**
   * Guardar cliente (crear o actualizar)
   */
  const saveCliente = useCallback(async () => {
    try {
      // Preparar datos para enviar a la API según el schema exacto del backend
      const clienteData = {
        // Información básica REQUERIDA
        full_name: cliente.fullName,
        email: cliente.email,
        phone: cliente.phone || null,
        mobile: cliente.mobile || null,
        phones: cliente.phones || null,

        // Tipo y estado
        client_type: cliente.clientType,
        status: cliente.status || 'lead',
        
        // Información de empresa (requerida si es company)
        company_name: cliente.companyName || '',
        tax_id: cliente.taxId || '',
        industry: cliente.industry || '',
        
        // Dirección de negocio
        business_address: cliente.businessAddress || '',
        business_unit: cliente.businessUnit || '',
        business_city: cliente.businessCity || '',
        business_state: cliente.businessState || '',
        business_country: cliente.businessCountry || 'Estados Unidos',
        business_postal_code: cliente.businessPostalCode || '',

        // Dirección de entrega
        same_delivery_address: cliente.sameDeliveryAddress !== false,
        delivery_address: cliente.sameDeliveryAddress !== false ? cliente.businessAddress : cliente.deliveryAddress,
        delivery_unit: cliente.sameDeliveryAddress !== false ? cliente.businessUnit : cliente.deliveryUnit,
        delivery_city: cliente.sameDeliveryAddress !== false ? cliente.businessCity : cliente.deliveryCity,
        delivery_state: cliente.sameDeliveryAddress !== false ? cliente.businessState : cliente.deliveryState,
        delivery_country: cliente.sameDeliveryAddress !== false ? cliente.businessCountry : cliente.deliveryCountry,
        delivery_postal_code: cliente.sameDeliveryAddress !== false ? cliente.businessPostalCode : cliente.deliveryPostalCode,
        is_residential: cliente.isResidential || false,
        
        // Categorización REQUERIDA
        client_category: cliente.clientCategory || null,
        business_type: cliente.businessType || 'BETEL_NATURAL',
        
        // Información de impuestos
        requires_taxes: cliente.requiresTaxes || false,
        
        // Información de pagos
        payment_type: cliente.paymentType || 'TERMINOS',
        payment_terms: cliente.paymentType === 'TERMINOS' ? (cliente.paymentTerms || 30) : 0,
        credit_limit: cliente.paymentType === 'TERMINOS' ? (cliente.creditLimit || 1000) : 0,
        credit_days: cliente.creditDays || 0,
        discount_percentage: cliente.discountPercentage || 0,
        
        // Tipo de envío
        shipping_type: cliente.shippingType || null,
        
        // Vendedor asignado REQUERIDO
        assigned_user_id: cliente.assignedUserId || null,
        
        // Seguimiento
        source: cliente.source || '',
        first_contact_date: cliente.firstContactDate || null,
        last_contact_date: cliente.lastContactDate || null,
        
        // Contactos adicionales
        additional_contacts: cliente.additionalContacts || [],
        
        // Metadatos
        notes: cliente.notes || '',
        tags: cliente.tags || '',
        
        // Portal
        portal_access: cliente.portalAccess || false
      };
      
      // Limpiar campos undefined/null donde no se permiten
      Object.keys(clienteData).forEach(key => {
        if (clienteData[key] === undefined) {
          delete clienteData[key];
        }
      });
      
      console.log('Datos a enviar a la API:', clienteData);
      
      let result;
      
      if (isEditing) {
        // Actualizar cliente existente
        result = await clientesService.updateCliente(clienteId, clienteData);
      } else {
        // Crear nuevo cliente
        result = await clientesService.createCliente(clienteData);
      }
      
      if (result.success) {
        return {
          success: true,
          message: result.message || `Cliente ${cliente.companyName || cliente.fullName} ${isEditing ? 'actualizado' : 'creado'} exitosamente`,
          data: result
        };
      } else {
        // Manejar errores de validación
        return {
          success: false,
          error: result.message || 'Error al guardar el cliente',
          validation_errors: result.validation_errors || [],
          validationErrors: result.validationErrors || {}
        };
      }
    } catch (err) {
      console.error('Error saving cliente:', err);
      return {
        success: false,
        error: 'Error de conexión al guardar el cliente'
      };
    }
  }, [cliente, isEditing, clienteId]);

  /**
   * Resetear formulario
   */
  const resetCliente = useCallback(() => {
    if (isEditing) {
      setCliente({ ...originalCliente });
    } else {
      setCliente({
        // Información básica REQUERIDA
        fullName: '',
        email: '',
        phone: '',
        mobile: '',
        
        // Tipo y estado
        clientType: 'individual',
        status: 'lead',
        
        // Información de empresa
        companyName: '',
        taxId: '',
        industry: '',
        
        // Dirección de negocio
        businessAddress: '',
        businessCity: '',
        businessState: '',
        businessCountry: 'Colombia',
        businessPostalCode: '',
        
        // Dirección de entrega
        sameDeliveryAddress: true,
        deliveryAddress: '',
        deliveryCity: '',
        deliveryState: '',
        deliveryCountry: '',
        deliveryPostalCode: '',
        
        // Categorización
        clientCategory: '',
        businessType: 'BETEL_NATURAL',
        
        // Impuestos y pagos
        requiresTaxes: false,
        paymentType: 'COD',
        paymentTerms: 0,
        creditLimit: 0,
        creditDays: 0,
        discountPercentage: 0,
        
        // Envío y asignación
        shippingType: '',
        assignedUserId: 1,
        
        // Seguimiento
        source: '',
        firstContactDate: null,
        lastContactDate: null,
        
        // Contactos y metadatos
        additionalContacts: [],
        notes: '',
        tags: '',
        portalAccess: false
      });
    }
    setHasChanges(false);
  }, [isEditing, originalCliente]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar cliente al montar (solo en modo edición)
  useEffect(() => {
    if (isEditing) {
      loadCliente();
    }
  }, [isEditing, loadCliente]);

  return {
    cliente,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateClienteData,
    saveCliente,
    resetCliente,
    clearError
  };
};